import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { timeOffApi, employeesApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Calendar as CalendarPicker } from '../components/ui/calendar';
import {
  Calendar,
  Clock,
  Plus,
  Check,
  X,
  Palmtree,
  Stethoscope,
  User,
  Loader2,
  Ban,
  Users,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';

// Stable className map for shadcn Calendar to avoid re-creating each render
const BLACKOUT_MODIFIER_CLASSES = {
  blackoutBlock: 'bg-red-100 text-red-700 line-through font-semibold',
  blackoutWarn: 'bg-amber-100 text-amber-700 font-medium',
};

const expandRange = (b) => {
  const out = [];
  const cur = new Date(b.start_date);
  const end = new Date(b.end_date);
  while (cur <= end) { out.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
  return out;
};

const TimeOffRequests = () => {
  const { user, selectedLocation } = useAuth();
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(null);
  const [reviewNote, setReviewNote] = useState('');
  const [reviewOverrideReason, setReviewOverrideReason] = useState('');

  // Rules / Settings
  const [showSettings, setShowSettings] = useState(false);
  const [blackouts, setBlackouts] = useState([]);
  const [limits, setLimits] = useState([]);
  const [newBlackout, setNewBlackout] = useState({ start_date: '', end_date: '', label: '', block: true });
  const [newLimit, setNewLimit] = useState({ date: '', max_off: 2, behavior: 'warn' });

  // Precheck on new request form
  const [precheck, setPrecheck] = useState(null);

  const blackoutModifiers = useMemo(
    () => ({
      blackoutBlock: blackouts.filter((b) => b.block).flatMap(expandRange),
      blackoutWarn: blackouts.filter((b) => !b.block).flatMap(expandRange),
    }),
    [blackouts]
  );

  const [newRequest, setNewRequest] = useState({
    type: 'vacation',
    start_date: '',
    end_date: '',
    reason: '',
  });

  const isEmployee = user?.role === 'employee';
  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin' || user?.role === 'accounting_admin' || user?.role === 'assistant_manager';
  const isAdmin = user?.role === 'admin' || user?.role === 'accounting_admin';
  const canManageRules = user?.role === 'admin' || user?.role === 'accounting_admin' || user?.role === 'manager' || user?.role === 'assistant_manager';

  useEffect(() => {
    loadData();
    if (canManageRules) loadSettings();
  }, [selectedLocation]);

  const loadSettings = async () => {
    try {
      const [bo, lim] = await Promise.all([
        timeOffApi.listBlackouts(),
        timeOffApi.listLimits(),
      ]);
      setBlackouts(bo);
      setLimits(lim);
    } catch (e) {
      console.error('Failed to load time-off settings:', e);
    }
  };

  const handleAddBlackout = async () => {
    if (!newBlackout.start_date) return;
    try {
      await timeOffApi.createBlackout({
        ...newBlackout,
        end_date: newBlackout.end_date || newBlackout.start_date,
        location_id: selectedLocation === 'all' ? null : selectedLocation,
      });
      setNewBlackout({ start_date: '', end_date: '', label: '', block: true });
      await loadSettings();
    } catch (e) {
      alert('Failed to add blackout: ' + e.message);
    }
  };

  const handleDeleteBlackout = async (id) => {
    if (!window.confirm('Delete this blackout?')) return;
    try {
      await timeOffApi.deleteBlackout(id);
      await loadSettings();
    } catch (e) {
      alert('Failed to delete: ' + e.message);
    }
  };

  const handleSaveLimit = async () => {
    try {
      await timeOffApi.upsertLimit({
        location_id: selectedLocation === 'all' ? null : selectedLocation,
        date: newLimit.date || null,
        max_off: Number(newLimit.max_off),
        behavior: newLimit.behavior,
      });
      setNewLimit({ date: '', max_off: 2, behavior: 'warn' });
      await loadSettings();
    } catch (e) {
      alert('Failed to save limit: ' + e.message);
    }
  };

  const handleDeleteLimit = async (id) => {
    if (!window.confirm('Delete this limit?')) return;
    try {
      await timeOffApi.deleteLimit(id);
      await loadSettings();
    } catch (e) {
      alert('Failed to delete: ' + e.message);
    }
  };

  const runPrecheck = async () => {
    if (!newRequest.start_date || !newRequest.end_date) {
      setPrecheck(null);
      return;
    }
    try {
      const res = await timeOffApi.precheck({
        locationId: user?.location_id || user?.locationId,
        startDate: newRequest.start_date,
        endDate: newRequest.end_date,
      });
      setPrecheck(res);
    } catch (e) {
      console.error(e);
      setPrecheck(null);
    }
  };

  useEffect(() => {
    if (showNewRequest) runPrecheck();
  }, [newRequest.start_date, newRequest.end_date, showNewRequest]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [reqs, emps] = await Promise.all([
        timeOffApi.getAll({ locationId: selectedLocation }),
        employeesApi.getAll(selectedLocation),
      ]);
      setRequests(reqs);
      setEmployees(emps);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRequests = useMemo(() => {
    let filtered = requests;
    if (activeTab !== 'all') {
      filtered = filtered.filter(r => r.status === activeTab);
    }
    return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [requests, activeTab]);

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  const handleSubmitRequest = async () => {
    try {
      await timeOffApi.create({
        ...newRequest,
        employee_id: user.id,
        location_id: user.location_id || user.locationId,
      });
      await loadData();
      setShowNewRequest(false);
      setNewRequest({ type: 'vacation', start_date: '', end_date: '', reason: '' });
    } catch (error) {
      alert('Failed to submit request: ' + error.message);
    }
  };

  const handleReviewRequest = async (status) => {
    if (!showReviewModal) return;
    try {
      const opts = {};
      if (reviewOverrideReason.trim() && status === 'approved') {
        opts.override = true;
        opts.override_reason = reviewOverrideReason.trim();
      }
      await timeOffApi.review(showReviewModal.id, status, reviewNote, opts);
      await loadData();
      setShowReviewModal(null);
      setReviewNote('');
      setReviewOverrideReason('');
    } catch (error) {
      alert('Failed to review request: ' + error.message);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'vacation': return <Palmtree className="w-4 h-4" />;
      case 'sick': return <Stethoscope className="w-4 h-4" />;
      case 'personal': return <User className="w-4 h-4" />;
      default: return <Calendar className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'vacation': return 'bg-blue-100 text-blue-700';
      case 'sick': return 'bg-red-100 text-red-700';
      case 'personal': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-purple-100 text-purple-700';
      case 'denied': return 'bg-red-100 text-red-700';
      case 'pending': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Time Off Requests</h1>
          <p className="text-gray-500 mt-1">
            {isEmployee ? 'Request and track your time off' : 'Manage employee time off requests'}
          </p>
        </div>
        {canManageRules && (
          <Button onClick={() => setShowSettings(true)} variant="outline" className="mr-2" data-testid="time-off-settings-btn">
            <Ban className="w-4 h-4 mr-2" />Blackouts & Limits
          </Button>
        )}
        {isEmployee && (
          <Button onClick={() => setShowNewRequest(true)} className="bg-purple-600 hover:bg-purple-700" data-testid="request-time-off-btn">
            <Plus className="w-4 h-4 mr-2" />
            Request Time Off
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Pending</p>
                <p className="text-3xl font-bold text-amber-600 mt-1">{pendingCount}</p>
              </div>
              <div className="bg-amber-100 p-3 rounded-xl">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Approved</p>
                <p className="text-3xl font-bold text-purple-600 mt-1">
                  {requests.filter(r => r.status === 'approved').length}
                </p>
              </div>
              <div className="bg-purple-100 p-3 rounded-xl">
                <Check className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Denied</p>
                <p className="text-3xl font-bold text-red-600 mt-1">
                  {requests.filter(r => r.status === 'denied').length}
                </p>
              </div>
              <div className="bg-red-100 p-3 rounded-xl">
                <X className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Requests</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{requests.length}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-xl">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Card className="mb-6 border-0 shadow-md">
        <CardContent className="p-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="pending" className="gap-2">
                <Clock className="w-4 h-4" />
                Pending {pendingCount > 0 && <Badge className="bg-amber-500 text-white ml-1">{pendingCount}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="approved">Approved</TabsTrigger>
              <TabsTrigger value="denied">Denied</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      {/* Requests List */}
      {filteredRequests.length > 0 ? (
        <div className="space-y-4">
          {filteredRequests.map((request) => {
            const emp = employees.find(e => e.id === request.employee_id);
            const days = differenceInDays(parseISO(request.end_date), parseISO(request.start_date)) + 1;
            
            return (
              <Card key={request.id} className="border-0 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      {!isEmployee && (
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={emp?.avatar} alt={emp?.name} />
                          <AvatarFallback className="bg-gray-200">
                            {emp?.name?.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {!isEmployee && <h3 className="font-semibold text-gray-900">{emp?.name}</h3>}
                          <Badge className={getTypeColor(request.type)}>
                            {getTypeIcon(request.type)}
                            <span className="ml-1 capitalize">{request.type}</span>
                          </Badge>
                          <Badge className={getStatusColor(request.status)}>{request.status}</Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {format(parseISO(request.start_date), 'MMM d')} - {format(parseISO(request.end_date), 'MMM d, yyyy')}
                          </span>
                          <span className="font-medium">{days} day{days > 1 ? 's' : ''}</span>
                        </div>
                        <p className="text-gray-600">{request.reason}</p>
                        {request.review_note && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-600"><span className="font-medium">Response:</span> {request.review_note}</p>
                          </div>
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                          Submitted {format(parseISO(request.created_at), 'MMM d, yyyy "at" h:mm a')}
                        </p>
                      </div>
                    </div>
                    {isManagerOrAdmin && request.status === 'pending' && (
                      <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => setShowReviewModal(request)}>
                        <Check className="w-4 h-4 mr-1" /> Review
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-0 shadow-md">
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">No time off requests found</p>
          </CardContent>
        </Card>
      )}

      {/* New Request Modal */}
      <Dialog open={showNewRequest} onOpenChange={setShowNewRequest}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request Time Off</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={newRequest.type} onValueChange={(value) => setNewRequest({ ...newRequest, type: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vacation"><span className="flex items-center gap-2"><Palmtree className="w-4 h-4" /> Vacation</span></SelectItem>
                  <SelectItem value="sick"><span className="flex items-center gap-2"><Stethoscope className="w-4 h-4" /> Sick Leave</span></SelectItem>
                  <SelectItem value="personal"><span className="flex items-center gap-2"><User className="w-4 h-4" /> Personal</span></SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={newRequest.start_date} onChange={(e) => setNewRequest({ ...newRequest, start_date: e.target.value })} data-testid="request-start-date" />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input type="date" value={newRequest.end_date} onChange={(e) => setNewRequest({ ...newRequest, end_date: e.target.value })} data-testid="request-end-date" />
              </div>
            </div>
            {/* Visual calendar showing blackout dates */}
            <div className="space-y-2" data-testid="blackout-calendar">
              <Label className="text-xs flex items-center gap-2">
                <Ban className="w-3.5 h-3.5 text-red-500" /> Blackout dates appear shaded in red
              </Label>
              <div className="border rounded-lg p-2 bg-gray-50 inline-block">
                <CalendarPicker
                  mode="range"
                  selected={
                    newRequest.start_date
                      ? { from: parseISO(newRequest.start_date), to: newRequest.end_date ? parseISO(newRequest.end_date) : parseISO(newRequest.start_date) }
                      : undefined
                  }
                  onSelect={(range) => {
                    if (!range) {
                      setNewRequest({ ...newRequest, start_date: '', end_date: '' });
                      return;
                    }
                    setNewRequest({
                      ...newRequest,
                      start_date: range.from ? format(range.from, 'yyyy-MM-dd') : '',
                      end_date: range.to ? format(range.to, 'yyyy-MM-dd') : (range.from ? format(range.from, 'yyyy-MM-dd') : ''),
                    });
                  }}
                  modifiers={blackoutModifiers}
                  modifiersClassNames={BLACKOUT_MODIFIER_CLASSES}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea value={newRequest.reason} onChange={(e) => setNewRequest({ ...newRequest, reason: e.target.value })} placeholder="Please provide a reason..." rows={3} />
            </div>
            {precheck && precheck.blackouts && precheck.blackouts.length > 0 && (
              <div className={`p-3 rounded-lg text-sm border ${precheck.block ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`} data-testid="precheck-blackout-warning">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">{precheck.block ? 'Blocked: blackout date(s) hit' : 'Heads up — blackout in range'}</p>
                    <ul className="list-disc ml-4 mt-1 text-xs">
                      {precheck.blackouts.map((b) => (
                        <li key={b.id}>{b.label || `${b.start_date} – ${b.end_date}`}{b.block ? ' (blocked)' : ' (warning)'}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            {precheck && precheck.limits && precheck.limits.some(l => l.limit_reached) && (
              <div className="p-3 rounded-lg text-sm border bg-amber-50 border-amber-200 text-amber-800" data-testid="precheck-limit-warning">
                <div className="flex items-start gap-2">
                  <Users className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Daily approved-off limit reached on:</p>
                    <ul className="list-disc ml-4 mt-1 text-xs">
                      {precheck.limits.filter(l => l.limit_reached).map(l => (
                        <li key={l.date}>{l.date} — {l.approved_count}/{l.max_off} ({l.behavior})</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewRequest(false)}>Cancel</Button>
            <Button
              onClick={handleSubmitRequest}
              className="bg-purple-600 hover:bg-purple-700"
              disabled={
                !newRequest.start_date ||
                !newRequest.end_date ||
                !newRequest.reason ||
                (precheck && precheck.block)
              }
              data-testid="submit-time-off-btn"
            >
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Modal */}
      <Dialog open={!!showReviewModal} onOpenChange={() => setShowReviewModal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Review Time Off Request</DialogTitle>
          </DialogHeader>
          {showReviewModal && (
            <div className="py-4">
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="font-medium text-gray-900">{employees.find(e => e.id === showReviewModal.employee_id)?.name}</p>
                <p className="text-sm text-gray-600 mt-1">{format(parseISO(showReviewModal.start_date), 'MMM d')} - {format(parseISO(showReviewModal.end_date), 'MMM d, yyyy')}</p>
                <p className="text-sm text-gray-600 mt-2">{showReviewModal.reason}</p>
              </div>
              <div className="space-y-2">
                <Label>Response Note (Optional)</Label>
                <Textarea value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} placeholder="Add a note..." rows={3} />
              </div>
              <div className="space-y-2 mt-3">
                <Label className="text-xs">Override reason (required only if approving a blocked blackout/limit)</Label>
                <Input value={reviewOverrideReason} onChange={(e) => setReviewOverrideReason(e.target.value)} placeholder="e.g., critical staffing need approved by HR" data-testid="review-override-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewModal(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleReviewRequest('denied')}><X className="w-4 h-4 mr-1" />Deny</Button>
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={() => handleReviewRequest('approved')}><Check className="w-4 h-4 mr-1" />Approve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Blackouts & Limits Settings Modal */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="time-off-settings-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Ban className="w-5 h-5 text-red-500" />Time-Off Blackouts & Daily Limits</DialogTitle>
          </DialogHeader>

          {!isAdmin && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
              <p className="font-semibold mb-0.5">Manager scope</p>
              <p>Rules you create will apply only to your assigned location. Switch the location selector in the header to manage rules for another assigned location.</p>
            </div>
          )}

          {!isAdmin && selectedLocation === 'all' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700" data-testid="manager-location-required-warning">
              <p className="font-semibold">Pick a specific location first</p>
              <p>Managers can&apos;t apply blackouts or limits across all locations. Use the location selector above to choose one of your assigned locations.</p>
            </div>
          )}

          <div className="py-2 space-y-6">
            {/* Blackouts */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Blackout Dates</h3>
              <p className="text-xs text-gray-500 mb-3">Dates when time-off requests are warned about or blocked.</p>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-3">
                <Input type="date" value={newBlackout.start_date} onChange={(e) => setNewBlackout({ ...newBlackout, start_date: e.target.value })} placeholder="Start" data-testid="blackout-start-input" />
                <Input type="date" value={newBlackout.end_date} onChange={(e) => setNewBlackout({ ...newBlackout, end_date: e.target.value })} placeholder="End (optional)" />
                <Input value={newBlackout.label} onChange={(e) => setNewBlackout({ ...newBlackout, label: e.target.value })} placeholder="Label (e.g., Christmas)" />
                <Select value={newBlackout.block ? 'block' : 'warn'} onValueChange={(v) => setNewBlackout({ ...newBlackout, block: v === 'block' })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="warn">Warn only</SelectItem>
                    <SelectItem value="block">Block requests</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleAddBlackout} disabled={!newBlackout.start_date || (!isAdmin && selectedLocation === 'all')} data-testid="add-blackout-btn">Add</Button>
              </div>
              <div className="space-y-1 max-h-56 overflow-y-auto border rounded p-2">
                {blackouts.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-3">No blackouts configured</p>
                ) : blackouts.map((b) => (
                  <div key={b.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded" data-testid={`blackout-row-${b.id}`}>
                    <div className="text-sm">
                      <span className="font-medium">{b.label || 'Blackout'}</span>
                      <span className="text-gray-500 ml-2">{b.start_date}{b.end_date !== b.start_date ? ` – ${b.end_date}` : ''}</span>
                      <Badge variant="outline" className={`ml-2 text-xs ${b.block ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>{b.block ? 'Block' : 'Warn'}</Badge>
                      {b.location_id ? <span className="ml-2 text-xs text-gray-400">loc: {b.location_id}</span> : <span className="ml-2 text-xs text-gray-400">all locations</span>}
                    </div>
                    <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDeleteBlackout(b.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Limits */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Max Employees Off Per Day</h3>
              <p className="text-xs text-gray-500 mb-3">Leave date empty for a default rule; set a date for an override.</p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
                <Input type="date" value={newLimit.date} onChange={(e) => setNewLimit({ ...newLimit, date: e.target.value })} placeholder="Date (or default)" data-testid="limit-date-input" />
                <Input type="number" min="0" value={newLimit.max_off} onChange={(e) => setNewLimit({ ...newLimit, max_off: e.target.value })} placeholder="Max off" data-testid="limit-max-input" />
                <Select value={newLimit.behavior} onValueChange={(v) => setNewLimit({ ...newLimit, behavior: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="warn">Warn only</SelectItem>
                    <SelectItem value="block">Block when full</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleSaveLimit} disabled={!isAdmin && selectedLocation === 'all'} data-testid="save-limit-btn">Save Rule</Button>
              </div>
              <div className="space-y-1 max-h-56 overflow-y-auto border rounded p-2">
                {limits.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-3">No limits configured</p>
                ) : limits.map((l) => (
                  <div key={l.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                    <div className="text-sm">
                      <span className="font-medium">{l.date || 'Default'}</span>
                      <span className="text-gray-500 ml-2">max {l.max_off} off</span>
                      <Badge variant="outline" className={`ml-2 text-xs ${l.behavior === 'block' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>{l.behavior}</Badge>
                      {l.location_id ? <span className="ml-2 text-xs text-gray-400">loc: {l.location_id}</span> : <span className="ml-2 text-xs text-gray-400">all locations</span>}
                    </div>
                    <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDeleteLimit(l.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeOffRequests;
